import {
  FormControl,
  FormControlProps,
  FormLabel,
  FormLabelProps,
  HStack,
  Text,
  TextProps,
} from '@chakra-ui/react';
import React from 'react';
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldArrayPath,
  FieldValues,
  Path,
} from 'react-hook-form';

import { TypeUtils } from '../../framework/utils/UtilityTypes';

/**
 * フォームアイテムモード
 */
export type FormItemMode = 'edit' | 'view';

export type ItemProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> | FieldArrayPath<S> = Path<S>,
> = {
  /**
   * アイテム名
   */
  name: N;
  /**
   * レンダーノード
   */
  renderNode?: React.FunctionComponent<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    field: ControllerRenderProps<S, any>;
  }>;
  /**
   * テキストプロパティ
   */
  textProps?: Partial<TextProps>;
  /**
   * テキスト値
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value?: FunctionType<ControllerRenderProps<S, any>, React.ReactNode>;
  /**
   * 非編集時ノード
   */
  viewNode?: React.FunctionComponent<{
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    field: ControllerRenderProps<S, any>;
  }>;
};

/**
 * フォームアイテムプロパティ
 */
export type FormItemProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> | FieldArrayPath<S> = Path<S>,
> = ItemProps<S, N> & {
  /**
   * コントロールオブジェクト
   */
  control: Control<S>;
  /**
   * フォームコントロールプロパティ
   */
  formControlProps?: FormControlProps;
  /**
   * フォームラベルプロパティ
   */
  formLabelProps?: FormLabelProps;
  /**
   * ラベル
   */
  label?: string;
  /**
   * フォームアイテムモード
   */
  mode?: FormItemMode;
  /**
   * 読み取り専用 (モードに依らない)
   */
  readonly?: boolean;
};

/**
 * フォームアイテム
 * @param props -
 * @returns
 */
const FormItem = <
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>,
>({
  renderNode: RenderNode,
  viewNode: ViewNode,
  ...props
}: FormItemProps<S, N>) => (
  <FormControl textAlign="left" {...props.formControlProps}>
    <FormLabel
      fontWeight="semibold"
      hidden={TypeUtils.isNil(props.label)}
      htmlFor={props.name}
      marginBottom={0}
      {...props.formLabelProps}
    >
      {props.label}
    </FormLabel>
    <Controller
      control={props.control}
      name={props.name}
      render={({ field }) =>
        !props.readonly &&
        props.mode === 'edit' &&
        !TypeUtils.isNil(RenderNode) ? (
          <RenderNode field={field} />
        ) : TypeUtils.isNil(ViewNode) ? (
          <Text textAlign="left" {...props.textProps}>
            {TypeUtils.isNil(props.value)
              ? TypeUtils.isNil(field.value)
                ? '―'
                : (field.value as any).toString()
              : props.value(field)}
          </Text>
        ) : (
          <ViewNode field={field} />
        )
      }
    />
  </FormControl>
);

/**
 * フォームアイテムプロパティ
 */
export type FormItemMultipleProps<
  S extends FieldValues = FieldValues,
  N extends Path<S> | FieldArrayPath<S> = Path<S>,
> = {
  /**
   * コントロールオブジェクト
   */
  control: Control<S>;
  delimiter?: React.ReactNode;
  /**
   * フォームコントロールプロパティ
   */
  formControlProps?: FormControlProps;
  /**
   * フォームラベルプロパティ
   */
  formLabelProps?: FormLabelProps;
  forms: ItemProps<S, N>[];
  /**
   * ラベル
   */
  label?: string;
  /**
   * フォームアイテムモード
   */
  mode?: FormItemMode;
  /**
   * 読み取り専用 (モードに依らない)
   */
  readonly?: boolean;
};

export const FormItemMultiple = <
  S extends FieldValues = FieldValues,
  N extends Path<S> = Path<S>,
>(
  props: FormItemMultipleProps<S, N>
) => (
  <FormControl textAlign="left" {...props.formControlProps}>
    <FormLabel
      fontWeight="semibold"
      hidden={TypeUtils.isNil(props.label)}
      // htmlFor={props.name}
      marginBottom={0}
      {...props.formLabelProps}
    >
      {props.label}
    </FormLabel>
    <HStack justifyContent="start">
      {props.forms.map(
        (
          { name, renderNode: RenderNode, textProps, viewNode: ViewNode },
          idx
        ) => (
          <>
            <Controller
              control={props.control}
              name={name}
              render={({ field }) =>
                !props.readonly &&
                props.mode === 'edit' &&
                !TypeUtils.isNil(RenderNode) ? (
                  <RenderNode field={field} />
                ) : TypeUtils.isNil(ViewNode) ? (
                  <Text textAlign="left" {...textProps}>
                    {TypeUtils.isNil(field.value)
                      ? '―'
                      : (field.value! as any).toString()}
                  </Text>
                ) : (
                  <ViewNode field={field} />
                )
              }
            />
            {idx < props.forms.length - 1 ? props.delimiter : null}
          </>
        )
      )}
    </HStack>
  </FormControl>
);

export default FormItem;
